import React, { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { formatDisplayValue } from "../../utils/runAgentResponse";
import "./Playground.css";

const EditableTable = ({ data, columns, onDataChange, onCommit }) => {
  const [tableData, setTableData] = useState(data || []);
  const [editingCell, setEditingCell] = useState(null);

  useEffect(() => {
    setTableData(data || []);
  }, [data]);

  const handleCellChange = (rowIndex, columnKey, value) => {
    const newData = [...tableData];
    newData[rowIndex] = {
      ...newData[rowIndex],
      [columnKey]: value,
    };
    setTableData(newData);
    if (onDataChange) {
      onDataChange(newData);
    }
  };

  const handleAddRow = () => {
    const newRow = {};
    columns.forEach((col) => {
      newRow[col.accessorKey] = "";
    });
    const newData = [...tableData, newRow];
    setTableData(newData);
    if (onDataChange) {
      onDataChange(newData);
    }
    if (onCommit) {
      onCommit(newData);
    }
  };

  const handleDeleteRow = (rowIndex) => {
    const newData = tableData.filter((_, index) => index !== rowIndex);
    setTableData(newData);
    if (onDataChange) {
      onDataChange(newData);
    }
    if (onCommit) {
      onCommit(newData);
    }
  };

  const handleCellBlur = () => {
    setEditingCell(null);
    if (onCommit) {
      onCommit(tableData);
    }
  };

  const handleCellFocus = (rowIndex, columnKey) => {
    setEditingCell(`${rowIndex}-${columnKey}`);
  };

  if (!tableData || tableData.length === 0) {
    return (
      <div className="editable-table-empty">
        <p>No data available</p>
        <button className="btn-add-row" onClick={handleAddRow}>
          <Plus size={16} />
          Add Row
        </button>
      </div>
    );
  }

  return (
    <div className="editable-table-container">
      <table className="editable-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.accessorKey}>{column.header || column.accessorKey}</th>
            ))}
            <th className="actions-column">Actions</th>
          </tr>
        </thead>
        <tbody>
          {tableData.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {columns.map((column) => {
                const cellKey = `${rowIndex}-${column.accessorKey}`;
                const isEditing = editingCell === cellKey;
                const rawValue = row[column.accessorKey];
                
                // Determine if this is a numeric field (unit_price, quantity, total, etc.)
                const isNumericField = ['unit_price', 'quantity', 'total', 'amount', 'tax_amount', 'tax_percentage', 'percentage', 'rate', 'percent'].includes(column.accessorKey.toLowerCase());
                
                let displayValue = formatDisplayValue(rawValue);
                if (isNumericField && isEditing) {
                  displayValue =
                    rawValue !== null && rawValue !== undefined
                      ? formatDisplayValue(rawValue)
                      : "";
                }
                
                return (
                  <td key={column.accessorKey}>
                    {isEditing ? (
                      <input
                        type={isNumericField ? "number" : "text"}
                        step={isNumericField ? "any" : undefined}
                        className="editable-cell-input"
                        value={displayValue}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          // Allow empty string, numbers, and decimal values
                          handleCellChange(rowIndex, column.accessorKey, newValue);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleCellBlur();
                          }
                          if (e.key === "Escape") {
                            e.preventDefault();
                            setEditingCell(null);
                          }
                        }}
                        onBlur={handleCellBlur}
                        autoFocus
                      />
                    ) : (
                      <div
                        className="editable-cell"
                        onClick={() => handleCellFocus(rowIndex, column.accessorKey)}
                        style={{ cursor: 'pointer' }}
                        title="Click to edit"
                      >
                        {column.cell
                          ? column.cell({ getValue: () => rawValue })
                          : formatDisplayValue(rawValue) || "-"}
                      </div>
                    )}
                  </td>
                );
              })}
              <td className="actions-cell">
                <button
                  className="btn-delete-row"
                  onClick={() => handleDeleteRow(rowIndex)}
                  title="Delete row"
                >
                  <Trash2 size={16} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button className="btn-add-row" onClick={handleAddRow}>
        <Plus size={16} />
        Add Row
      </button>
    </div>
  );
};

export default EditableTable;

